import type { RouteRecordRaw } from 'vue-router';

import LegacyShellHost from '@/platform/layout/LegacyShellHost.vue';
import { LEGACY_NESTED_ONLY_PATHS, LEGACY_OWNED_PATHS } from '@celerdata/host-bridge-contract';

const nestedOnly = new Set<string>(LEGACY_NESTED_ONLY_PATHS);

// Legacy sub-app routes fall into two groups:
//   1. Chrome-variant routes (canvas / minimal / …) — declared explicitly
//      below with their own `meta.layout`. These are NOT in
//      LEGACY_OWNED_PATHS because the shared list only enumerates paths
//      that render with default chrome (sidebar + flashbar + trial banner).
//   2. Default-chrome routes — one bare-prefix record + one depth-wildcard
//      record generated for each entry in LEGACY_OWNED_PATHS. Both are
//      emitted because vue-router does not treat `${prefix}/:pathMatch(.*)*`
//      as matching the bare `${prefix}` (the wildcard requires at least one
//      segment) — a single record would leave `/clusters` unmatched.
// After the explicit + generated records, a terminal redirect sends
// anything else to `/404`; when legacy's catch-all forwards an unmatched
// path up the bridge, host committing to it here lands on that redirect.
const legacyRoutes: RouteRecordRaw[] = [
  // The sub sees this as /add-cluster (platform/host-sdk/legacy-path-alias.ts).
  {
    path: '/add-cluster/old',
    name: 'legacy-add-cluster-old',
    component: LegacyShellHost,
    meta: { renderer: 'legacy', layout: 'canvas' },
  },
  // Bare `/` landing — separate record because vue-router's `:pathMatch(.*)*`
  // requires a non-empty subpath when nested under a parent.
  {
    path: '',
    name: 'legacy-root',
    component: LegacyShellHost,
    meta: { renderer: 'legacy' },
  },
  // Default-chrome legacy prefixes — generated at build time from the
  // shared LEGACY_OWNED_PATHS declaration. Migrating a prefix out of
  // legacy = removing it from that list; these records regenerate
  // without it and the URL no longer resolves to LegacyShellHost.
  ...LEGACY_OWNED_PATHS.flatMap<RouteRecordRaw>((prefix) => [
    // Skipped for a nested-only prefix, whose index page the host owns.
    ...(nestedOnly.has(prefix)
      ? []
      : ([
          {
            path: prefix,
            name: `legacy-${prefix.slice(1)}`,
            component: LegacyShellHost,
            meta: { renderer: 'legacy' },
          },
        ] satisfies RouteRecordRaw[])),
    {
      path: `${prefix}/:pathMatch(.*)*`,
      name: `legacy-${prefix.slice(1)}-nested`,
      component: LegacyShellHost,
      meta: { renderer: 'legacy' },
    },
  ]),
  // Terminal redirect: anything not matched by an explicit or generated
  // record above falls to /404 (native, routes/errors.ts). Keep this LAST.
  {
    path: '/:pathMatch(.*)*',
    name: 'legacy-terminal-404',
    redirect: '/404',
  },
];

export default legacyRoutes;
